/* iter-86 — Two-step email change modal.

  Step 1: New address → POST /api/auth/me/email/change → server emails a
          6-digit code to the new address.
  Step 2: 6 single-digit boxes → POST /api/auth/me/email/change/confirm
          → server rotates the email + invalidates the JWT; we drop the
          local token and route the user back to /auth.

Props:
  • open     — controls the dialog
  • onClose  — close-button + cancel
  • onRotated(newEmail) — fires after a successful rotation so the parent
                          can sign the user out / show a relogin notice.

Resilience:
  • If the modal is re-opened with a pending request still active, we
    GET /api/auth/me/email/change/pending and jump straight to Step 2.
*/

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ChevronRight, X, ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangeEmailDialog({ open, onClose, currentEmail, onRotated }) {
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const boxes = useRef([]);

  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Reset on open + check for resumable pending request.
  useEffect(() => {
    if (!open) {
      setStep(1); setNewEmail(''); setRequestId(null); setCode(['', '', '', '', '', '']);
      setBusy(false); setResendCountdown(0);
      return;
    }
    axios.get(`${API}/auth/me/email/change/pending`, auth)
      .then(({ data }) => {
        if (data && data.id) {
          setRequestId(data.id);
          setNewEmail(data.new_email || '');
          setStep(2);
          toast.info('Resuming your pending email change.');
        }
      })
      .catch(() => { /* no pending — fine */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const t = setTimeout(() => setResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const initiate = async () => {
    const candidate = newEmail.trim().toLowerCase();
    if (!candidate) { toast.error('Enter your new email address.'); return; }
    if (candidate === (currentEmail || '').toLowerCase()) {
      toast.error("That's already your email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/me/email/change`, { new_email: candidate }, auth);
      setRequestId(data.request_id);
      setStep(2);
      setResendCountdown(60);
      // Dev fallback — server returns the code only when EXPOSE_DEV_CODES is set.
      if (data.code) {
        toast.info(`Dev code: ${data.code}`);
      } else {
        toast.success(`Code sent to ${candidate}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the email change.');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    const codeStr = code.join('');
    if (codeStr.length !== 6) { toast.error('Enter the 6-digit code.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/me/email/change/confirm`,
        { request_id: requestId, code: codeStr },
        auth,
      );
      toast.success(`Email updated to ${data.new_email}. You will be signed out — please log in again.`);
      // Old JWT is invalidated server-side. Drop the local token so the
      // next API call goes through the auth page cleanly.
      try { localStorage.removeItem('admin_token'); } catch { /* noop */ }
      onRotated?.(data.new_email);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not confirm the code.';
      toast.error(detail);
      if (e?.response?.status === 410) {
        // expired — reset to step 1
        setStep(1); setRequestId(null); setCode(['', '', '', '', '', '']);
      }
    } finally { setBusy(false); }
  };

  const cancelPending = async () => {
    if (!requestId) { onClose(); return; }
    try {
      await axios.delete(`${API}/auth/me/email/change/${requestId}`, auth);
    } catch { /* noop */ }
    setStep(1); setRequestId(null); setCode(['', '', '', '', '', '']);
    onClose();
  };

  const updateCodeBox = (i, val) => {
    // Accept paste of the full 6-digit code in any box.
    const digits = (val || '').replace(/\D+/g, '');
    if (!digits) {
      setCode((arr) => { const next = [...arr]; next[i] = ''; return next; });
      return;
    }
    if (digits.length >= 6) {
      const six = digits.slice(0, 6).split('');
      setCode(six.concat(Array(6 - six.length).fill('')).slice(0, 6));
      boxes.current[5]?.focus();
      return;
    }
    setCode((arr) => { const next = [...arr]; next[i] = digits[0]; return next; });
    if (i < 5) boxes.current[i + 1]?.focus();
  };

  const onCodeKey = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      boxes.current[i - 1]?.focus();
    } else if (e.key === 'Enter' && code.join('').length === 6) {
      confirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancelPending(); }}>
      <DialogContent data-testid="change-email-dialog" className="w-[95vw] max-w-md p-0" style={{ backgroundColor: '#F4F1EB' }}>
        <div className="p-5 sm:p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
              <Mail className="w-5 h-5" style={{ color: '#003DA5' }} />
              Change email address
            </DialogTitle>
            <p className="text-[11px] uppercase tracking-wider font-bold mt-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              Step {step} of 2 — {step === 1 ? 'New address' : 'Verify'}
            </p>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                  New email
                </label>
                <input
                  autoFocus
                  type="email"
                  data-testid="change-email-input"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') initiate(); }}
                  placeholder="new.email@example.com"
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ borderColor: '#A3D4D4', backgroundColor: 'white', fontFamily: 'Manrope' }}
                />
                <p className="text-[11px] mt-2" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  We'll send a 6-digit code to the new address to confirm you own it. Your current login is{' '}
                  <strong style={{ color: '#1A2E2E' }}>{currentEmail}</strong>.
                </p>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={cancelPending}
                  data-testid="change-email-cancel"
                  className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
                  style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button
                  onClick={initiate}
                  disabled={busy || !newEmail.trim()}
                  data-testid="change-email-submit"
                  className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
                  style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
                >
                  {busy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>) : (<>Send code <ChevronRight className="w-3.5 h-3.5" /></>)}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-[12px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                Code sent to <strong style={{ color: '#1A2E2E' }}>{newEmail}</strong>. Enter the 6-digit code below.
              </p>
              <div className="flex items-center justify-center gap-1.5" data-testid="change-email-code-boxes">
                {code.map((c, i) => (
                  <input
                    key={i}
                    ref={(el) => { boxes.current[i] = el; }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={c}
                    onChange={(e) => updateCodeBox(i, e.target.value)}
                    onKeyDown={(e) => onCodeKey(i, e)}
                    data-testid={`change-email-code-${i}`}
                    className="w-10 h-12 text-center rounded-lg border text-lg font-bold outline-none"
                    style={{ borderColor: '#A3D4D4', backgroundColor: 'white', color: '#003DA5', fontFamily: '"Courier New",monospace' }}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between text-[11px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                <button
                  onClick={() => { setStep(1); setCode(['', '', '', '', '', '']); }}
                  data-testid="change-email-back"
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  <ArrowLeft className="w-3 h-3" /> Use a different email
                </button>
                <button
                  onClick={initiate}
                  disabled={resendCountdown > 0 || busy}
                  data-testid="change-email-resend"
                  className="hover:underline disabled:no-underline disabled:opacity-50"
                >
                  {resendCountdown > 0 ? `Resend in 0:${String(resendCountdown).padStart(2, '0')}` : 'Resend code'}
                </button>
              </div>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={cancelPending}
                  data-testid="change-email-cancel-step2"
                  className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
                  style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button
                  onClick={confirm}
                  disabled={busy || code.join('').length !== 6}
                  data-testid="change-email-confirm"
                  className="flex-1 px-4 py-2.5 rounded-lg text-xs font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-60"
                  style={{ backgroundColor: 'linear-gradient(135deg,#10B981,#0D9488)', background: 'linear-gradient(135deg,#10B981,#0D9488)', fontFamily: 'Manrope' }}
                >
                  {busy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Confirming…</>) : (<><ShieldCheck className="w-3.5 h-3.5" /> Confirm change</>)}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
